import {
    useState,
    useEffect,
    useLayoutEffect,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Header from '../shared/components/Header';
import ConfirmModal from '../shared/components/ConfirmModal';
import { SOLICITUD_ALTERNATIVAS_API } from '../shared/services/solicitudAlternativas';
import { fetchSolicitudAlternativas } from '../../../../redux/solicitudAlternativas';
import SolicitudAlternativasTable from './components/SolicitudAlternativasTable';
import SolicitudAlternativasModals from './components/SolicitudAlternativasModals';
import { PAGE_SIZE } from './utils';

const MODAL_CLOSED = {
    type: null,
    item: null,
    intent: 'ver',
};

function buildSearchQuery(searcher) {
    const mapping = {
        key: searcher?.key,
        search: searcher?.search,
        start_date: searcher?.start_date,
        end_date: searcher?.end_date,
        recambista: searcher?.responsable,
        at_cliente: searcher?.creado_por,
        estado: searcher?.estado,
    };
    const params = new URLSearchParams();
    Object.entries(mapping).forEach(([key, value]) => {
        if (value) params.append(key, value);
    });
    const qs = params.toString();
    return qs ? `&${qs}` : '';
}

export default function SolicitudAlternativas() {
    const dispatch = useDispatch();
    const { page } = useParams();
    const currPage = Number(page) || 1;
    const navigate = useNavigate();
    const searcher = useSelector((state) => state.search);
    const user = JSON.parse(localStorage.getItem('user'));
    const userId = user?.id;
    const userRolId = Number(user?.rol_id);
    const solicitud_alternativas = useSelector(
        (state) => state?.solicitud_alternativas,
    );

    const [data, setData] = useState({ items: [], total_items: 0 });
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(MODAL_CLOSED);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const recambistaDefaultApplied = useRef(false);

    useLayoutEffect(() => {
        if (!userId || userRolId !== 2 || recambistaDefaultApplied.current) {
            return;
        }
        if (
            searcher?.responsable != null &&
            searcher.responsable !== ''
        ) {
            recambistaDefaultApplied.current = true;
            return;
        }
        recambistaDefaultApplied.current = true;
        dispatch({
            type: 'search/set',
            payload: {
                ...(searcher && typeof searcher === 'object' ? searcher : {}),
                key: searcher?.key || 'presupuesto',
                search: searcher?.search ?? '',
                responsable: Number(userId),
            },
        });
    }, [userId, userRolId, dispatch]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await SOLICITUD_ALTERNATIVAS_API.get(
                    currPage,
                    buildSearchQuery(searcher),
                );
                if (cancelled) return;
                if (res?.success) {
                    setData({
                        items: res.data.items || [],
                        total_items: res.data.total_items || 0,
                    });
                } else {
                    toast.error('No se ha podido obtener el listado.');
                }
            } catch (e) {
                if (!cancelled) {
                    toast.error(e?.message || 'Ha ocurrido un error.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [currPage, searcher]);

    useEffect(() => {
        const len = Array.isArray(solicitud_alternativas)
            ? solicitud_alternativas.length
            : 0;
        if (len === 0) {
            dispatch(fetchSolicitudAlternativas());
        }
    }, [dispatch, solicitud_alternativas]);

    const openModal = useCallback((type, item = null, intent = 'ver') => {
        if (type === 'detalle') {
            setModal({ type: 'detalle', item, intent });
            return;
        }
        if (type === 'new') {
            setModal({ type: 'new', item: null, intent: 'ver' });
            return;
        }
        setModal({ type, item, intent: 'ver' });
    }, []);

    const closeModal = useCallback(() => setModal(MODAL_CLOSED), []);

    const closeDeleteConfirm = useCallback(() => {
        setDeleteTarget(null);
    }, []);

    const handleDeleteConfirm = useCallback(async (confirmed) => {
        if (!confirmed || !deleteTarget) {
            setDeleteTarget(null);
            return;
        }

        try {
            dispatch({ type: 'loading/set', payload: true });
            const res = await SOLICITUD_ALTERNATIVAS_API.remove(deleteTarget.id);
            if (!res?.success) {
                toast.error(res?.message || 'No se ha podido borrar la solicitud.');
                return;
            }

            toast.success('Solicitud borrada correctamente.');
            setData((prev) => ({
                ...prev,
                items: prev.items.filter((row) => row.id !== deleteTarget.id),
                total_items: Math.max((prev.total_items || 0) - 1, 0),
            }));
        } catch (e) {
            toast.error(
                e?.response?.data?.message ||
                    e?.message ||
                    'Error al borrar la solicitud.',
            );
        } finally {
            dispatch({ type: 'loading/set', payload: false });
            setDeleteTarget(null);
        }
    }, [deleteTarget, dispatch]);

    const handleAction = useCallback(
        (actionId, rowItem) => {
            const handlers = {
                responder: () => openModal('responder', rowItem),
                asignar: () => openModal('asignar', rowItem),
                detalle: () => openModal('detalle', rowItem, 'ver'),
                aportar_informacion: () =>
                    openModal('detalle', rowItem, 'aportar'),
                delete: () => setDeleteTarget(rowItem),
                sin_alternativa: () => openModal('sin_alternativa', rowItem),
                nota_interna: () => openModal('nota_interna', rowItem),
            };
            handlers[actionId]?.();
        },
        [openModal],
    );

    const updateRow = useCallback((updatedItem) => {
        setData((prev) => ({
            ...prev,
            items: prev.items.map((row) =>
                row.id === updatedItem.id ? { ...row, ...updatedItem } : row,
            ),
        }));
    }, []);

    const hasItems = useMemo(
        () => (data.items?.length ?? 0) > 0,
        [data.items],
    );

    const paginationCount = useMemo(
        () => Math.ceil((data.total_items || 0) / PAGE_SIZE) || 0,
        [data.total_items],
    );

    const handleSearch = (searchData) => {
        navigate(`/solicitud-alternativas/${1}`);
        dispatch({ type: 'search/set', payload: searchData });
    };

    const handleChangePage = (event, newPage) => {
        navigate(`/solicitud-alternativas/${newPage}`);
    };

    const handleNewSolicitudCreated = (newItem) => {
        if (currPage !== 1) {
            navigate('/solicitud-alternativas/1');
            return;
        }
        setData((prev) => {
            const alreadyInList = prev.items.some((row) => row.id === newItem.id);
            if (alreadyInList) {
                return {
                    ...prev,
                    items: prev.items.map((row) =>
                        row.id === newItem.id ? { ...row, ...newItem } : row,
                    ),
                };
            }
            return {
                ...prev,
                items: [newItem, ...prev.items].slice(0, PAGE_SIZE),
                total_items: prev.total_items + 1,
            };
        });
    };

    return (
        <div className="content pt-3">
            <Header
                viewSearch={true}
                onSearch={handleSearch}
                onNew={() => openModal('new')}
            />

            <SolicitudAlternativasTable
                loading={loading}
                hasItems={hasItems}
                items={data.items}
                onAction={handleAction}
                currPage={currPage}
                paginationCount={paginationCount}
                onPageChange={handleChangePage}
            />

            <SolicitudAlternativasModals
                modal={modal}
                closeModal={closeModal}
                onUpdateRow={updateRow}
                onNewCreated={handleNewSolicitudCreated}
            />

            {deleteTarget ? (
                <ConfirmModal
                    title="Borrar solicitud"
                    description="¿Seguro que quieres borrar esta solicitud alternativa?"
                    state={Boolean(deleteTarget)}
                    onConfirmAction={handleDeleteConfirm}
                    onClose={closeDeleteConfirm}
                />
            ) : null}
        </div>
    );
}
